/*
 * The Calendar destination's state.
 *
 * This destination is the industry calendar — other people's concerts, so an organizer can avoid
 * booking into a weekend that already has three festivals in it. The frozen frame titles it
 * "Calendar & Milestones" and draws a planning calendar instead: milestones, task deadlines and the
 * show schedule on a month grid, with a collision count.
 *
 * Milestones do not exist as a record anywhere, and cues carry a time of day but no date, so
 * neither can be placed on a grid. What *can* be placed, and what makes the frame's collision count
 * mean something, is the event's own dated commitments — task deadlines and the show itself —
 * checked against the concerts already in the calendar. `collisionsOn` is that check: it is the one
 * question this destination exists to answer, asked of the organizer's own dates rather than left
 * for them to eyeball.
 */
import { useCallback, useEffect, useState } from 'react';
import { adminApi, ApiError, opsApi } from '../../../api';
import type { ConcertCalendarDto, OpsTaskDto } from '../../../api';
import { eventDayIn } from './eventDate';

/** The day a calendar row falls on, as `YYYY-MM-DD`, or null where the row cannot be placed. */
export function dayOf(concert: ConcertCalendarDto): string | null {
  const iso = concert.sortDate?.slice(0, 10);
  return iso && /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso : null;
}

export interface DatedCommitment {
  id: string;
  /** `YYYY-MM-DD`. */
  date: string;
  /**
   * `milestone` is a task somebody marked as one of the dates the event is measured by — the same
   * record, read differently. Kept distinct from `task` here because the frame draws them as the
   * dates that matter, and burying them among every deadline would defeat that.
   */
  kind: 'show' | 'milestone' | 'task';
  label: string;
  /** Who owns it, where the record names someone. */
  owner: string | null;
  /** Other concerts on the same day. The reason this screen exists. */
  collisions: ConcertCalendarDto[];
  /** Only ever true for a milestone: a finished deadline is dropped, a passed milestone is kept. */
  done?: boolean;
}

/**
 * The organizer's own dated commitments, each with the concerts that share its day.
 *
 * Only unfinished tasks are listed: a deadline that has already been met is not a date anyone needs
 * to defend. The show itself is always listed when the event has an end date, because it is the one
 * commitment that cannot move.
 *
 * A **finished milestone is kept**, unlike a finished task. The two are read for different reasons:
 * a deadline list answers "what still needs doing", and a milestone list answers "what is this
 * event's shape" — and a shape with the passed dates deleted is not a shape.
 */
export function commitmentsOf(
  tasks: readonly OpsTaskDto[],
  concerts: readonly ConcertCalendarDto[],
  eventEndsAt: string | null,
  eventTimezone: string | null = null,
): DatedCommitment[] {
  const byDay = new Map<string, ConcertCalendarDto[]>();
  for (const concert of concerts) {
    const day = dayOf(concert);
    if (!day) continue;
    byDay.set(day, [...(byDay.get(day) ?? []), concert]);
  }

  const out: DatedCommitment[] = [];

  if (eventEndsAt) {
    // The event's own day, not UTC's: a show ending just after midnight in Bangkok is on the next
    // day there and the previous one in UTC, and this marker would land on the wrong square.
    const day = eventDayIn(eventEndsAt, eventTimezone) ?? eventEndsAt.slice(0, 10);
    out.push({
      id: 'show',
      date: day,
      kind: 'show',
      label: 'วันแสดงของงานนี้',
      owner: null,
      collisions: byDay.get(day) ?? [],
    });
  }

  for (const task of tasks) {
    if (task.status === 'done' && !task.isMilestone) continue;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(task.slaDate)) continue;
    out.push({
      id: task.id,
      date: task.slaDate,
      kind: task.isMilestone ? 'milestone' : 'task',
      label: task.title,
      owner: task.departmentName || task.ownerName || task.importedOwnerName || null,
      collisions: byDay.get(task.slaDate) ?? [],
      done: task.status === 'done',
    });
  }

  return out.sort((a, b) => a.date.localeCompare(b.date));
}

export interface CalendarState {
  concerts: ConcertCalendarDto[];
  tasks: OpsTaskDto[];
  eventEndsAt: string | null;
  eventTimezone: string | null;
  commitments: DatedCommitment[];
  loading: boolean;
  loadError: string;
  /** Set when the ops half could not be read — the concert grid still stands on its own. */
  tasksUnavailable: boolean;
  reload: () => Promise<void>;
}

export function useEventCalendar(
  token: string,
  eventSlug: string | undefined,
  role: string | null,
): CalendarState {
  const [concerts, setConcerts] = useState<ConcertCalendarDto[] | null>(null);
  const [tasks, setTasks] = useState<OpsTaskDto[]>([]);
  const [tasksUnavailable, setTasksUnavailable] = useState(false);
  /*
   * The show date rides on the ops settings record — the one Event Admin read that carries
   * `events.ends_at`. It is fetched with the tasks, under the same permission, so a token that
   * cannot read ops simply gets no marker rather than an error.
   */
  const [eventEnds, setEventEnds] = useState<{ at: string | null; timezone: string | null }>(
    { at: null, timezone: null },
  );
  const [loadError, setLoadError] = useState('');

  const load = useCallback(async () => {
    try {
      const rows = await adminApi.getConcertCalendar(token, eventSlug);
      setConcerts(rows);
      setLoadError('');
    } catch (e) {
      setLoadError(e instanceof ApiError ? e.message : 'โหลดปฏิทินคอนเสิร์ตไม่สำเร็จ');
      return;
    }

    /*
     * The organizer's own dates are a second, optional read. A sales token is refused the ops
     * family, and this screen is still worth showing without them — so a failure here narrows what
     * the page can say rather than replacing it with an error.
     */
    if (role === 'sales') {
      setTasksUnavailable(true);
    } else {
      try {
        const [rows, settings] = await Promise.all([
          opsApi.listTasks(token, eventSlug),
          opsApi.getEventSettings(token, eventSlug),
        ]);
        setTasks(rows);
        setEventEnds({ at: settings.eventEndsAt, timezone: settings.eventTimezone });
        setTasksUnavailable(false);
      } catch {
        setTasksUnavailable(true);
      }
    }

    }, [token, eventSlug, role]);

  useEffect(() => { void load(); }, [load]);

  return {
    concerts: concerts ?? [],
    tasks,
    eventEndsAt: eventEnds.at,
    eventTimezone: eventEnds.timezone,
    commitments: commitmentsOf(tasks, concerts ?? [], eventEnds.at, eventEnds.timezone),
    loading: concerts === null && !loadError,
    loadError,
    tasksUnavailable,
    reload: load,
  };
}
